import { Select, useScreenWidth } from "@msys/ui";
import { Box, Grid, Slider, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useField } from "formik";
import { clamp, isNil, isString, keyBy, sortBy } from "lodash-es";
import React from "react";
import { useLatest } from "react-use";
import { AttachmentSnapshot } from "../../../clients/graphqlTypes.js";
import { FormattedFloatOptionalField } from "../../commons/form-fields/FormattedFloatOptionalField.js";
import {
  SelectBooleanField,
  SelectField,
  SelectNumberField,
  SelectOption,
} from "../../commons/form-fields/SelectField.js";
import {
  SelectMultipleField,
  SelectMultipleNumberField,
} from "../../commons/form-fields/SelectMultipleField.js";
import { TextField } from "../../commons/form-fields/TextField.js";
import { assertNever } from "../../utils.js";
import {
  Props2NonComputedAll_Props2Bool_Fragment,
  Props2NonComputedAll_Props2Number_Fragment,
  Props2NonComputedAll_Props2NumberArray_Fragment,
  Props2NonComputedAll_Props2Text_Fragment,
  Props2NonComputedAll_Props2TextArray_Fragment,
  Props2NonComputedAllFragment,
  Props2NumberRangeFragment,
} from "./properties.generated.js";
import {
  getPropertyDisplayedUnit,
  isValueNumberInNumberRange,
} from "./properties.js";
import { PropertyOptionItem } from "./PropertyOptionItem.js";
import { usePropertyHelpers } from "./usePropertyHelpers.js";

interface Props {
  name: string;
  property: Props2NonComputedAllFragment;
  required?: boolean;
  disabled?: boolean;
  valueLabels?: { value: string; label: string }[];
  showSelectThreshold?: number;
  additionalRanges?: Props2NumberRangeFragment[];
}

export const WizardPropertyField = ({
  name,
  property,
  required = false,
  disabled = false,
  valueLabels,
  showSelectThreshold,
  additionalRanges,
}: Props) => {
  switch (property.__typename) {
    case "Props2Bool":
      return (
        <PropertyBoolField
          key={property.key}
          name={name}
          property={property}
          required={required}
          disabled={disabled}
        />
      );
    case "Props2Text":
      if (property.allowedValuesText && property.allowedValuesText.length > 0) {
        return (
          <PropertyTextWithAllowedValuesField
            key={property.key}
            name={name}
            property={property}
            required={required}
            disabled={disabled}
            valueLabels={valueLabels}
            showSelectThreshold={showSelectThreshold}
          />
        );
      }
      return (
        <PropertyTextField
          key={property.key}
          name={name}
          property={property}
          required={required}
          disabled={disabled}
        />
      );
    case "Props2Number":
      if (
        property.allowedValuesNumber &&
        property.allowedValuesNumber.length > 0
      ) {
        if (additionalRanges && additionalRanges.length > 0) {
          return (
            <PropertyNumberFieldWithAdditionalRangesAndAllowedValues
              key={property.key}
              name={name}
              property={property}
              disabled={disabled}
              required={required}
              additionalRanges={additionalRanges}
            />
          );
        }
        return (
          <PropertyNumberWithAllowedValuesField
            key={property.key}
            name={name}
            property={property}
            required={required}
            disabled={disabled}
            showSelectThreshold={showSelectThreshold}
          />
        );
      }
      if (additionalRanges && additionalRanges.length > 0) {
        return (
          <PropertyNumberFieldWithAdditionalRanges
            key={property.key}
            name={name}
            property={property}
            disabled={disabled}
            required={required}
            additionalRanges={additionalRanges}
          />
        );
      }
      return (
        <PropertyNumberField
          key={property.key}
          name={name}
          property={property}
          disabled={disabled}
          required={required}
        />
      );
    case "Props2TextArray":
      if (property.allowedValuesText && property.allowedValuesText.length > 0) {
        return (
          <PropertyTextArrayWithAllowedValuesField
            key={property.key}
            name={name}
            property={property}
            required={required}
            disabled={disabled}
            showSelectThreshold={showSelectThreshold}
          />
        );
      }
      return null;
    case "Props2NumberArray":
      if (
        property.allowedValuesNumber &&
        property.allowedValuesNumber.length > 0
      ) {
        return (
          <PropertyNumberArrayWithAllowedValuesField
            key={property.key}
            name={name}
            property={property}
            required={required}
            disabled={disabled}
            showSelectThreshold={showSelectThreshold}
          />
        );
      }
      return null;
  }
};

const PropertyTextField = ({
  name,
  property,
  required,
  disabled,
}: {
  name: string;
  property: Props2NonComputedAll_Props2Text_Fragment;
  required?: boolean;
  disabled?: boolean;
}) => {
  const { t } = useTranslate(["Global", "Properties"]);
  return (
    <Stack direction="column" spacing={1}>
      <Typography variant="h3" color="secondary">
        {`${property.prompt || property.label}${required ? "*" : ""}`}
      </Typography>
      <TextField
        label={t("Enter the value", {
          ns: "Properties",
        })}
        type="text"
        name={`${name}.valueText`}
        disabled={disabled}
      />
    </Stack>
  );
};

const PropertyNumberInputField = ({
  name,
  property,
  disabled,
  prepareActualValue,
}: {
  name: string;
  property: Props2NonComputedAll_Props2Number_Fragment;
  disabled?: boolean;
  prepareActualValue?: (value: number) => number;
}) => {
  const { t } = useTranslate(["Global", "Properties"]);
  const { getUnitLabel } = usePropertyHelpers();
  return (
    <Stack direction="row" spacing={1}>
      <FormattedFloatOptionalField
        label={t("Enter the value", { ns: "Properties" })}
        name={`${name}.valueNumber`}
        type="float"
        min={property.range?.min ?? undefined}
        max={property.range?.max ?? undefined}
        step={property.step ?? 0.01}
        // unit={property.unit ? "\u00A0" + property.unit : undefined}
        sx={{ maxWidth: "150px", flexShrink: 0, flexGrow: 0 }}
        disabled={disabled}
        prepareActualValue={prepareActualValue}
      />
      {property.unit && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "48px",
          }}
        >
          <Typography variant="body2" color="secondary">
            {getUnitLabel(property.unit)}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

const PropertyNumberSliderField = ({
  name,
  property,
  disabled,
  min,
  max,
}: {
  name: string;
  property: Props2NonComputedAll_Props2Number_Fragment;
  disabled?: boolean;
  min: number;
  max: number;
}) => {
  const [{ value }, { touched, error: formError }, { setValue: setFormValue }] =
    useField<number | null>(`${name}.valueNumber`);
  const { getNumberLabel } = usePropertyHelpers();
  return (
    <Stack
      direction="row"
      spacing={3}
      sx={{
        pt: 1,
        alignItems: "center",
      }}
    >
      <Box>
        <Typography variant="body2">{getNumberLabel(min)}</Typography>
      </Box>
      <Slider
        color="secondary"
        value={value ?? min}
        onChange={async (event, newValue) => {
          if (typeof newValue === "number") {
            await setFormValue(newValue);
          }
        }}
        valueLabelDisplay="auto"
        min={min}
        max={max}
        step={property.step ?? 1}
        disabled={disabled}
      />
      <Box>
        <Typography variant="body2">{getNumberLabel(max)}</Typography>
      </Box>
    </Stack>
  );
};

const PropertyNumberField = ({
  name,
  property,
  required,
  disabled,
}: {
  name: string;
  property: Props2NonComputedAll_Props2Number_Fragment;
  required?: boolean;
  disabled?: boolean;
}) => {
  return (
    <Stack direction="column" spacing={1}>
      <Typography variant="h3" color="secondary">
        {`${property.prompt || property.label}${required ? "*" : ""}`}
      </Typography>
      <PropertyNumberInputField
        name={name}
        property={property}
        disabled={disabled}
      />
      {property.range &&
      !isNil(property.range.min) &&
      !isNil(property.range.max) ? (
        <PropertyNumberSliderField
          name={name}
          property={property}
          disabled={disabled}
          min={property.range.min}
          max={property.range.max}
        />
      ) : null}
    </Stack>
  );
};

const PropertyNumberFieldWithAdditionalRanges = ({
  name,
  property,
  required,
  disabled,
  additionalRanges,
}: {
  name: string;
  property: Props2NonComputedAll_Props2Number_Fragment;
  required?: boolean;
  disabled?: boolean;
  additionalRanges: Props2NumberRangeFragment[];
}) => {
  const { t } = useTranslate(["Global", "Properties"]);
  const [{ value }, { touched, error }, { setValue: setFormValue }] = useField<
    number | null
  >(`${name}.valueNumber`);
  const { getUnitLabel, getNumberLabel } = usePropertyHelpers();

  const rangeOptions = React.useMemo((): Props2NumberRangeFragment[] => {
    return sortBy(
      [...(property.range ? [property.range] : []), ...additionalRanges],
      r => r.min ?? r.max ?? 0
    );
  }, [additionalRanges, property.range]);

  const [rangeIndex, setRangeIndex] = React.useState<number>(() => {
    if (!isNil(value)) {
      const index = rangeOptions.findIndex(r =>
        isValueNumberInNumberRange(value, r)
      );
      if (index >= 0) return index;
    }
    return 0;
  });

  const range = rangeOptions[rangeIndex] ?? rangeOptions[0];
  const propertyWithRange = { ...property, range };

  const rangeIndexLatest = useLatest(rangeIndex);
  const rangeOptionsLatest = useLatest(rangeOptions);

  React.useEffect(() => {
    // reset to 0
    if (
      rangeIndexLatest.current > 0 &&
      rangeIndexLatest.current >= rangeOptions.length
    ) {
      setRangeIndex(0);
      return;
    }
  }, [rangeIndexLatest, rangeOptions.length]);

  // React.useEffect(() => {
  //   // select correct range
  //   if (!isNil(value)) {
  //     const index = rangeOptionsLatest.current.findIndex(r =>
  //       isValueNumberInNumberRange(value, r)
  //     );
  //     if (index >= 0 && index !== rangeIndexLatest.current) {
  //       setRangeIndex(index);
  //       return;
  //     }
  //   }
  // }, [rangeIndexLatest, rangeOptionsLatest, value]);

  const prepareActualValue = (newValue: number) => {
    // 1. adjust to global min/max range
    const { min, max } = rangeOptions.reduce(
      (acc, r) => {
        return adjustToRange(acc, r);
      },
      { min: null, max: null } as { min: number | null; max: number | null }
    );
    newValue = clampToRange(newValue, { min, max });

    // 2. check if any ranges matches
    const rangeMatchIndex = rangeOptions.findIndex(r =>
      isValueNumberInNumberRange(newValue, r)
    );
    if (rangeMatchIndex >= 0) return newValue;

    // 3. if no range matches - clamp to the current range
    if (range) {
      return clampToRange(newValue, range);
    }
    return newValue;
  };

  return (
    <Stack direction="column" spacing={1}>
      <Typography variant="h3" color="secondary">
        {`${property.prompt || property.label}${required ? "*" : ""}`}
      </Typography>
      <Stack direction="row" spacing={1}>
        <Select
          fullWidth
          label={t("Select option", { ns: "Properties" })}
          onChange={value => {
            setRangeIndex(parseInt(value, 10));
          }}
          value={rangeIndex.toString()}
          options={Array(rangeOptions.length)
            .fill(undefined)
            .map((v, idx) => ({
              value: idx.toString(),
              label: getRangeLabel(
                rangeOptions[idx],
                getNumberLabel,
                property.unit ? getUnitLabel(property.unit) : ""
              ),
            }))}
          disabled={disabled}
        />
        <Box
          sx={{
            display: "flex",
            flexShrink: 0,
            flexGrow: 0,
          }}
        >
          <PropertyNumberInputField
            name={name}
            property={propertyWithRange}
            disabled={disabled}
            prepareActualValue={prepareActualValue}
          />
        </Box>
      </Stack>
      {propertyWithRange.range &&
      !isNil(propertyWithRange.range.min) &&
      !isNil(propertyWithRange.range.max) ? (
        <PropertyNumberSliderField
          name={name}
          property={propertyWithRange}
          disabled={disabled}
          min={propertyWithRange.range.min}
          max={propertyWithRange.range.max}
        />
      ) : null}
    </Stack>
  );
};

const PropertyNumberFieldWithAdditionalRangesAndAllowedValues = ({
  name,
  property,
  required,
  disabled,
  additionalRanges,
}: {
  name: string;
  property: Props2NonComputedAll_Props2Number_Fragment;
  required?: boolean;
  disabled?: boolean;
  additionalRanges: Props2NumberRangeFragment[];
}) => {
  const { t } = useTranslate(["Global", "Properties"]);
  const [{ value }, { touched, error }, { setValue: setFormValue }] = useField<
    number | null
  >(`${name}.valueNumber`);
  const { getUnitLabel, getNumberLabel } = usePropertyHelpers();

  const allowedValuesOptions = React.useMemo(
    () =>
      property.allowedValuesNumber!.map(({ allowedNumber }) => ({
        value: allowedNumber,
        label: `${getNumberLabel(allowedNumber)}${getPropertyDisplayedUnit(
          property,
          getUnitLabel
        )}`,
      })),
    [property, getNumberLabel, getUnitLabel]
  );
  const rangeOptions = React.useMemo((): Props2NumberRangeFragment[] => {
    return [...(property.range ? [property.range] : []), ...additionalRanges];
  }, [additionalRanges, property.range]);

  const selectOptions = React.useMemo(
    () =>
      sortBy(
        [
          ...allowedValuesOptions.map(av => ({
            type: "value" as const,
            value: av,
          })),
          ...rangeOptions.map(r => ({ type: "range" as const, value: r })),
        ],
        v => {
          switch (v.type) {
            case "value":
              return v.value.value;
            case "range":
              return v.value.min ?? v.value.max ?? 0;
            default:
              assertNever(v);
          }
        }
      ),
    [allowedValuesOptions, rangeOptions]
  );

  const [optionIndex, setOptionIndex] = React.useState<number | null>(() => {
    if (!isNil(value)) {
      const valueMatchIndex = selectOptions.findIndex(
        o => o.type === "value" && o.value.value === value
      );
      if (valueMatchIndex >= 0) return valueMatchIndex;
      const rangeMatchIndex = selectOptions.findIndex(
        o => o.type === "range" && isValueNumberInNumberRange(value, o.value)
      );
      if (rangeMatchIndex >= 0) return rangeMatchIndex;

      // when actual value is defined, show first range
      const firstRangeIndex = selectOptions.findIndex(o => o.type === "range");
      if (firstRangeIndex >= 0) return firstRangeIndex;
    }
    return null;
  });

  const range =
    !isNil(optionIndex) && selectOptions[optionIndex]?.type === "range"
      ? selectOptions[optionIndex].value
      : null;
  const allowedValue =
    !isNil(optionIndex) && selectOptions[optionIndex]?.type === "value"
      ? selectOptions[optionIndex].value
      : null;

  const propertyWithRange = range ? { ...property, range } : property;

  const optionIndexLatest = useLatest(optionIndex);
  const valueLatest = useLatest(value);
  const selectOptionsLatest = useLatest(selectOptions);

  const selectOptionsStringified = selectOptions
    .map(v => {
      switch (v.type) {
        case "value":
          return `value:${v.value}`;
        case "range":
          return rangeToString(v.value);
        default:
          assertNever(v);
      }
    })
    .join("/");

  React.useEffect(() => {
    // set to exact allowed value when select options are getting changed
    if (!isNil(valueLatest.current) && selectOptionsLatest.current.length > 0) {
      const newIndex = selectOptionsLatest.current.findIndex(
        o => o.type === "value" && o.value.value === valueLatest.current
      );
      if (newIndex >= 0 && newIndex !== optionIndexLatest.current) {
        setOptionIndex(newIndex);
        return;
      }
    }

    // reset to null
    if (
      !isNil(optionIndexLatest.current) &&
      optionIndexLatest.current > 0 &&
      optionIndexLatest.current >= selectOptionsLatest.current.length
    ) {
      setOptionIndex(null);
      return;
    }
  }, [selectOptionsStringified]);

  // React.useEffect(() => {
  //   // select correct range/value
  //   if (!isNil(value)) {
  //     const valueMatchIndex = selectOptionsLatest.current.findIndex(
  //       o => o.type === "value" && o.value.value === value
  //     );
  //     const rangeMatchIndex = selectOptionsLatest.current.findIndex(
  //       o => o.type === "range" && isValueNumberInNumberRange(value, o.value)
  //     );
  //     if (
  //       valueMatchIndex >= 0 &&
  //       rangeMatchIndex < 0 &&
  //       valueMatchIndex !== optionIndexLatest.current
  //     ) {
  //       setOptionIndex(valueMatchIndex);
  //       return;
  //     }
  //     if (
  //       rangeMatchIndex >= 0 &&
  //       valueMatchIndex < 0 &&
  //       rangeMatchIndex !== optionIndexLatest.current
  //     ) {
  //       setOptionIndex(rangeMatchIndex);
  //       return;
  //     }
  //     if (
  //       rangeMatchIndex >= 0 &&
  //       valueMatchIndex >= 0 &&
  //       valueMatchIndex !== optionIndexLatest.current &&
  //       rangeMatchIndex !== optionIndexLatest.current
  //     ) {
  //       setOptionIndex(valueMatchIndex);
  //       return;
  //     }
  //   }
  // }, [optionIndexLatest, selectOptionsLatest, value]);

  const prepareActualValue = (newValue: number) => {
    // 1. adjust to global min/max range
    const { min, max } = selectOptions.reduce(
      (acc, o) => {
        if (o.type === "range") {
          return adjustToRange(acc, o.value);
        }
        if (o.type === "value") {
          return adjustToValue(acc, o.value.value);
        }
        return acc;
      },
      { min: null, max: null } as { min: number | null; max: number | null }
    );
    newValue = clampToRange(newValue, { min, max });

    // 2. check if any options matches
    const valueMatchIndex = selectOptions.findIndex(
      o => o.type === "value" && o.value.value === newValue
    );
    if (valueMatchIndex >= 0) return newValue;
    const rangeMatchIndex = selectOptions.findIndex(
      o => o.type === "range" && isValueNumberInNumberRange(newValue, o.value)
    );
    if (rangeMatchIndex >= 0) return newValue;

    // 3. if no options matches - clamp to the current range / allowed value
    if (range) {
      return clampToRange(newValue, range);
    }
    if (allowedValue) return allowedValue.value;
    return newValue;
  };

  return (
    <Stack direction="column" spacing={1}>
      <Typography variant="h3" color="secondary">
        {`${property.prompt || property.label}${required ? "*" : ""}`}
      </Typography>
      <Stack direction="row" spacing={1}>
        <Select
          fullWidth
          label={t("Select option", { ns: "Properties" })}
          onChange={async value => {
            const newIndex = parseInt(value, 10);
            const newValue = selectOptions[newIndex];
            setOptionIndex(newIndex);
            if (newValue && newValue.type === "value") {
              await setFormValue(newValue.value.value);
            }
          }}
          value={!isNil(optionIndex) ? optionIndex.toString() : ""}
          options={Array(selectOptions.length)
            .fill(undefined)
            .map((_, idx) => {
              const v = selectOptions[idx];
              return {
                value: idx.toString(),
                label: (() => {
                  switch (v.type) {
                    case "value":
                      return v.value.label;
                    case "range":
                      return getRangeLabel(
                        v.value,
                        getNumberLabel,
                        property.unit ? getUnitLabel(property.unit) : ""
                      );
                    default:
                      assertNever(v);
                  }
                })(),
              };
            })}
          clearable={Boolean(allowedValue)}
          onClear={
            allowedValue
              ? async () => {
                  setOptionIndex(null);
                  await setFormValue(null);
                }
              : undefined
          }
          disabled={disabled}
          error={!range && error && isString(error) ? error : undefined}
        />
        {range && (
          <Box
            sx={{
              display: "flex",
              flexShrink: 0,
              flexGrow: 0,
            }}
          >
            <PropertyNumberInputField
              name={name}
              property={propertyWithRange}
              disabled={disabled}
              prepareActualValue={prepareActualValue}
            />
          </Box>
        )}
      </Stack>
      {range &&
      propertyWithRange &&
      propertyWithRange.range &&
      !isNil(propertyWithRange.range.min) &&
      !isNil(propertyWithRange.range.max) ? (
        <PropertyNumberSliderField
          name={name}
          property={propertyWithRange}
          disabled={disabled}
          min={propertyWithRange.range.min}
          max={propertyWithRange.range.max}
        />
      ) : null}
    </Stack>
  );
};

const PropertyBoolField = ({
  name,
  property,
  required,
  disabled,
  showSelectThreshold,
}: {
  name: string;
  property: Props2NonComputedAll_Props2Bool_Fragment;
  required?: boolean;
  disabled?: boolean;
  showSelectThreshold?: number;
}) => {
  const { getBoolLabel } = usePropertyHelpers();
  const options = React.useMemo(
    () =>
      [true, false].map(value => ({
        value,
        label: getBoolLabel(value),
      })),
    [getBoolLabel]
  );

  return (
    <Stack direction="column" spacing={1}>
      <Typography variant="h3" color="secondary">
        {`${property.prompt || property.label}${required ? "*" : ""}`}
      </Typography>
      <OptionsSelect<boolean>
        name={`${name}.valueBool`}
        options={options}
        type="bool"
        disabled={disabled}
        showSelectThreshold={showSelectThreshold}
      />
    </Stack>
  );
};

const PropertyTextWithAllowedValuesField = ({
  name,
  property,
  required,
  disabled,
  valueLabels,
  showSelectThreshold,
}: {
  name: string;
  property: Props2NonComputedAll_Props2Text_Fragment;
  required?: boolean;
  disabled?: boolean;
  valueLabels?: { value: string; label: string }[];
  showSelectThreshold?: number;
}) => {
  const options = React.useMemo(() => {
    const valueLabelsByValue = keyBy(valueLabels ?? [], e => e.value);

    return property.allowedValuesText!.map(({ allowedText, media }) => {
      const valueLabel: string | undefined =
        valueLabelsByValue[allowedText]?.label;
      return {
        value: allowedText,
        label: valueLabel ?? allowedText,
        image: media ?? null,
      };
    });
  }, [property.allowedValuesText, valueLabels]);

  return (
    <Stack direction="column" spacing={1}>
      <Typography variant="h3" color="secondary">
        {`${property.prompt || property.label}${required ? "*" : ""}`}
      </Typography>
      <OptionsSelect<string>
        name={`${name}.valueText`}
        options={options}
        type="text"
        disabled={disabled}
        showSelectThreshold={showSelectThreshold}
      />
    </Stack>
  );
};

const PropertyNumberWithAllowedValuesField = ({
  name,
  property,
  required,
  disabled,
  showSelectThreshold,
}: {
  name: string;
  property: Props2NonComputedAll_Props2Number_Fragment;
  required?: boolean;
  disabled?: boolean;
  showSelectThreshold?: number;
}) => {
  const { getUnitLabel, getNumberLabel } = usePropertyHelpers();
  const options = React.useMemo(
    () =>
      property.allowedValuesNumber!.map(({ allowedNumber, media }) => ({
        value: allowedNumber,
        label: `${getNumberLabel(allowedNumber)}${getPropertyDisplayedUnit(
          property,
          getUnitLabel
        )}`,
        image: media ?? null,
      })),
    [property, getNumberLabel, getUnitLabel]
  );

  return (
    <Stack direction="column" spacing={1}>
      <Typography variant="h3" color="secondary">
        {`${property.prompt || property.label}${required ? "*" : ""}`}
      </Typography>
      <OptionsSelect<number>
        name={`${name}.valueNumber`}
        options={options}
        type="number"
        disabled={disabled}
        showSelectThreshold={showSelectThreshold}
      />
    </Stack>
  );
};

const PropertyTextArrayWithAllowedValuesField = ({
  name,
  property,
  required,
  disabled,
  showSelectThreshold,
}: {
  name: string;
  property: Props2NonComputedAll_Props2TextArray_Fragment;
  required?: boolean;
  disabled?: boolean;
  showSelectThreshold?: number;
}) => {
  const options = React.useMemo(
    () =>
      property.allowedValuesText!.map(({ allowedText, media }) => ({
        value: allowedText,
        label: allowedText,
        image: media ?? null,
      })),
    [property.allowedValuesText]
  );

  return (
    <Stack direction="column" spacing={1}>
      <Typography variant="h3" color="secondary">
        {`${property.prompt || property.label}${required ? "*" : ""}`}
      </Typography>
      <OptionsSelectMultiple<string>
        type="text"
        name={`${name}.valueTextArray`}
        options={options}
        disabled={disabled}
        showSelectThreshold={showSelectThreshold}
      />
    </Stack>
  );
};

const PropertyNumberArrayWithAllowedValuesField = ({
  name,
  property,
  required,
  disabled,
  showSelectThreshold,
}: {
  name: string;
  property: Props2NonComputedAll_Props2NumberArray_Fragment;
  required?: boolean;
  disabled?: boolean;
  showSelectThreshold?: number;
}) => {
  const { getUnitLabel, getNumberLabel } = usePropertyHelpers();
  const options = React.useMemo(
    () =>
      property.allowedValuesNumber!.map(({ allowedNumber, media }) => ({
        value: allowedNumber,
        label: `${getNumberLabel(allowedNumber)}${getPropertyDisplayedUnit(
          property,
          getUnitLabel
        )}`,
        image: media ?? null,
      })),
    [property, getNumberLabel, getUnitLabel]
  );

  return (
    <Stack direction="column" spacing={1}>
      <Typography variant="h3" color="secondary">
        {`${property.prompt || property.label}${required ? "*" : ""}`}
      </Typography>
      <OptionsSelectMultiple<number>
        type="number"
        name={`${name}.valueNumberArray`}
        options={options}
        disabled={disabled}
        showSelectThreshold={showSelectThreshold}
      />
    </Stack>
  );
};

const OptionsSelect = <V,>({
  type,
  name,
  options,
  disabled,
  showSelectThreshold = 10,
}: {
  name: string;
  options: { value: V; label: string; image?: AttachmentSnapshot | null }[];
  type: "number" | "text" | "bool";
  disabled?: boolean;
  showSelectThreshold?: number;
}) => {
  const { t } = useTranslate(["Global", "Properties"]);
  const [{ value }, , { setValue: setFormValue }] = useField<V | null>(name);
  const { isMinTablet } = useScreenWidth();
  const withImage = options.some(o => o.image);

  return options.length <= showSelectThreshold || withImage ? (
    <Box>
      <Grid container spacing={1} columns={isMinTablet ? 3 : 2}>
        {options.map((option, index) => (
          <Grid key={index} size={1}>
            <PropertyOptionItem
              label={option.label}
              isPreselected={value === option.value}
              isGreyedOut={value !== option.value}
              image={option.image ?? null}
              withImage={withImage}
              onClick={async () => {
                setFormValue(value === option.value ? null : option.value);
              }}
              disabled={disabled}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  ) : type === "number" ? (
    <SelectNumberField
      name={name}
      options={options as SelectOption<number>[]}
      label={t("Select the value", {
        ns: "Properties",
      })}
      clearable={!disabled}
      onClear={() => setFormValue(null)}
      disabled={disabled}
    />
  ) : type === "bool" ? (
    <SelectBooleanField
      name={name}
      options={options as SelectOption<boolean>[]}
      label={t("Select the value", {
        ns: "Properties",
      })}
      clearable={!disabled}
      onClear={() => setFormValue(null)}
      disabled={disabled}
    />
  ) : (
    <SelectField
      name={name}
      options={options}
      label={t("Select the value", {
        ns: "Properties",
      })}
      clearable={!disabled}
      onClear={() => setFormValue(null)}
      disabled={disabled}
    />
  );
};

const OptionsSelectMultiple = <V,>({
  type,
  name,
  options,
  disabled,
  showSelectThreshold = 10,
}: {
  name: string;
  options: { value: V; label: string; image?: AttachmentSnapshot | null }[];
  type: "number" | "text";
  disabled?: boolean;
  showSelectThreshold?: number;
}) => {
  const { t } = useTranslate(["Global", "Properties"]);
  const [{ value }, , { setValue: setFormValue }] = useField<V[] | null>(name);
  const { isMinTablet } = useScreenWidth();
  const withImage = options.some(o => o.image);

  return options.length <= showSelectThreshold || withImage ? (
    <Box>
      <Grid container spacing={1} columns={isMinTablet ? 3 : 2}>
        {options.map((option, index) => (
          <Grid key={index} size={1}>
            <PropertyOptionItem
              multiple
              label={option.label}
              isPreselected={value?.includes(option.value) ?? false}
              isGreyedOut={!value?.includes(option.value)}
              image={option.image ?? null}
              withImage={withImage}
              onClick={() => {
                setFormValue(
                  value?.includes(option.value)
                    ? value.filter(v => v !== option.value)
                    : [...(value ?? []), option.value]
                );
              }}
              disabled={disabled}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  ) : type === "number" ? (
    <SelectMultipleNumberField
      name={name}
      options={options as SelectOption<number>[]}
      label={t("Select the value", {
        ns: "Properties",
      })}
      disabled={disabled}
    />
  ) : (
    <SelectMultipleField
      name={name}
      options={options}
      label={t("Select the value", {
        ns: "Properties",
      })}
      disabled={disabled}
    />
  );
};

function formatNumberWithUnit(number: string, unitLabel: string) {
  return `${number}${unitLabel.length > 3 ? ` ${unitLabel}` : unitLabel}`;
}

function getRangeLabel(
  range: Props2NumberRangeFragment,
  getNumberLabel: (n: number) => string,
  unitLabel: string
) {
  if (!isNil(range.min) && !isNil(range.max)) {
    return `${formatNumberWithUnit(getNumberLabel(range.min), unitLabel)} – ${formatNumberWithUnit(getNumberLabel(range.max), unitLabel)}`;
  }
  if (!isNil(range.min)) {
    return `≥ ${formatNumberWithUnit(getNumberLabel(range.min), unitLabel)}`;
  }
  if (!isNil(range.max)) {
    return `≤ ${formatNumberWithUnit(getNumberLabel(range.max), unitLabel)}`;
  }
  return "";
}

function rangeToString(range: Props2NumberRangeFragment) {
  if (!isNil(range.min) && !isNil(range.max)) {
    return `range:${range.min}-${range.max}`;
  }
  if (!isNil(range.min)) {
    return `min:${range.min}`;
  }
  if (!isNil(range.max)) {
    return `max:${range.max}`;
  }
  return "";
}

function clampToRange(
  value: number,
  {
    min,
    max,
  }: { min?: number | null | undefined; max?: number | null | undefined }
) {
  if (!isNil(min) && !isNil(max)) {
    return clamp(value, min, max);
  }
  if (!isNil(min)) {
    return Math.max(value, min);
  }
  if (!isNil(max)) {
    return Math.min(value, max);
  }
  return value;
}

function adjustToRange(
  input: {
    min: number | null;
    max: number | null;
  },
  {
    min,
    max,
  }: { min?: number | null | undefined; max?: number | null | undefined }
) {
  const range = { ...input };
  if (!isNil(min)) {
    range.min = range.min === null ? min : Math.min(range.min, min);
  }
  if (!isNil(max)) {
    range.max = range.max === null ? max : Math.max(range.max, max);
  }
  return range;
}

function adjustToValue(
  input: {
    min: number | null;
    max: number | null;
  },
  value: number | null | undefined
) {
  const range = { ...input };
  if (!isNil(value)) {
    range.min = range.min === null ? value : Math.min(range.min, value);
    range.max = range.max === null ? value : Math.max(range.max, value);
  }
  return range;
}
